import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import LoadingSpinner from '@/components/layout/LoadingSpinner'
import { useToast } from '@/hooks/use-toast'
import { Trophy, Medal, Award, Crown, TrendingUp, Users, Gift, Heart } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

interface LeaderboardEntry {
  id: string
  full_name: string
  email: string
  visit_stamps: number
  referral_stamps: number
  total_visits: number
  total_referrals: number
  points: number
  rank?: number
  created_at: string
}

interface LeaderboardStats {
  totalCustomers: number
  averageVisits: number
  averageReferrals: number
  topReferrer: string | null
  currentUserRank: number | null
}

const Leaderboard = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [currentUser, setCurrentUser] = useState<LeaderboardEntry | null>(null)
  const [stats, setStats] = useState<LeaderboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'visits' | 'referrals' | 'points'>('visits')

  useEffect(() => {
    fetchLeaderboardData()
  }, [user, activeTab])

  const fetchLeaderboardData = async () => {
    if (!user?.email) return

    try {
      // Get current user's customer data first
      const { data: userData, error: userError } = await supabase
        .from('customers')
        .select('*')
        .eq('email', user.email)
        .single()

      if (userError) throw userError

      setCurrentUser(userData)

      // Get all customers from the same tenant
      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select('id, full_name, email, visit_stamps, referral_stamps, total_visits, total_referrals, points, created_at')
        .eq('tenant_id', userData.tenant_id)
        .order(getOrderColumn(), { ascending: false })
        .limit(50)

      if (customersError) throw customersError

      // Add ranks and process data
      const processedData = customers.map((customer, index) => ({
        ...customer,
        rank: index + 1
      }))

      setLeaderboard(processedData)

      // Calculate stats
      const totalCustomers = customers.length
      const averageVisits = Math.round(customers.reduce((sum, c) => sum + c.visit_stamps, 0) / totalCustomers)
      const averageReferrals = Math.round(customers.reduce((sum, c) => sum + c.referral_stamps, 0) / totalCustomers)
      const topReferrer = customers.sort((a, b) => b.total_referrals - a.total_referrals)[0]?.full_name || null
      const currentUserRank = processedData.find(c => c.id === userData.id)?.rank || null

      setStats({
        totalCustomers,
        averageVisits,
        averageReferrals,
        topReferrer,
        currentUserRank
      })

    } catch (error) {
      console.error('Error fetching leaderboard:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Leaderboard konnte nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const getOrderColumn = () => {
    switch (activeTab) {
      case 'referrals': return 'total_referrals'
      case 'points': return 'points'
      default: return 'total_visits'
    }
  }

  const getTabValue = (customer: LeaderboardEntry) => {
    switch (activeTab) {
      case 'referrals': return customer.total_referrals
      case 'points': return customer.points
      default: return customer.total_visits
    }
  }

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Crown className="h-6 w-6 text-yellow-500" />
      case 2: return <Medal className="h-6 w-6 text-gray-400" />
      case 3: return <Award className="h-6 w-6 text-yellow-600" />
      default: return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>
    }
  }

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'referrals': return <Users className="h-4 w-4" />
      case 'points': return <Gift className="h-4 w-4" />
      default: return <Heart className="h-4 w-4" />
    }
  }

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case 'referrals': return 'Empfehlungen'
      case 'points': return 'Punkte'
      default: return 'Besuche'
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">🏆 Leaderboard</h1>
          <p className="text-muted-foreground">
            Wer sammelt die meisten Stempel? Finden Sie es heraus!
          </p>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="flex items-center p-6">
                <Users className="h-8 w-8 text-primary mr-3" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalCustomers}</p>
                  <p className="text-sm text-muted-foreground">Teilnehmer</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center p-6">
                <Heart className="h-8 w-8 text-accent mr-3" />
                <div>
                  <p className="text-2xl font-bold">{stats.averageVisits}</p>
                  <p className="text-sm text-muted-foreground">Ø Besuche</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center p-6">
                <TrendingUp className="h-8 w-8 text-success mr-3" />
                <div>
                  <p className="text-2xl font-bold">{stats.currentUserRank || '-'}</p>
                  <p className="text-sm text-muted-foreground">Ihr Rang</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center p-6">
                <Crown className="h-8 w-8 text-warning mr-3" />
                <div>
                  <p className="text-lg font-bold truncate">{stats.topReferrer || 'Keiner'}</p>
                  <p className="text-sm text-muted-foreground">Top Empfehler</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6">
          {(['visits', 'referrals', 'points'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {getTabIcon(tab)}
              {getTabLabel(tab)}
            </button>
          ))}
        </div>

        {/* Current User Position */}
        {currentUser && stats?.currentUserRank && (
          <Card className="mb-6 border-primary/50 bg-primary/5">
            <CardHeader>
              <CardTitle className="text-lg">Ihre Position</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                    {getRankIcon(stats.currentUserRank)}
                  </div>
                  <div>
                    <p className="font-semibold">{currentUser.full_name || currentUser.email}</p>
                    <p className="text-sm text-muted-foreground">
                      {getTabValue(currentUser)} {getTabLabel(activeTab)}
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-lg px-4 py-2">
                  Rang #{stats.currentUserRank}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Rangliste - {getTabLabel(activeTab)}
            </CardTitle>
            <CardDescription>
              Top Performer in der Kategorie {getTabLabel(activeTab).toLowerCase()}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3">
              {leaderboard.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Daten verfügbar</h3>
                  <p className="text-muted-foreground">
                    Es sind noch keine Leaderboard-Daten vorhanden.
                  </p>
                </div>
              ) : (
                leaderboard.map((customer) => (
                  <div
                    key={customer.id}
                    className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                      customer.id === currentUser?.id
                        ? 'bg-primary/10 border border-primary/20'
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12">
                        {getRankIcon(customer.rank!)}
                      </div>
                      
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-accent/20 text-accent">
                          {(customer.full_name || customer.email)
                            .substring(0, 2)
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div>
                        <p className="font-semibold">
                          {customer.full_name || customer.email}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Mitglied seit {format(new Date(customer.created_at), 'MMM yyyy', { locale: de })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">
                        {getTabValue(customer)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {activeTab === 'visits' && `${customer.visit_stamps} aktive Stempel`}
                        {activeTab === 'referrals' && `${customer.referral_stamps} Referral-Stempel`}
                        {activeTab === 'points' && `${customer.total_visits} Besuche`}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default Leaderboard